import { Button } from "@/components/ui/button";
import { ArrowRight, BadgeCheck, TrendingUp } from "lucide-react";

interface EmployerHeroProps {
  onRequestDemo: () => void;
}

const EmployerHero = ({ onRequestDemo }: EmployerHeroProps) => {
  return (
    <section className="relative py-24 md:py-36 bg-[#0D1B2A] overflow-hidden">
      {/* Premium gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A] via-[#1B3A4B] to-[#0D1B2A]" />
      
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,219,77,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,219,77,0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />
      
      {/* Accent glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#ffdb4d]/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Executive badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-2 rounded-full mb-8">
            <BadgeCheck className="w-4 h-4 text-[#ffdb4d]" />
            <span className="text-sm font-medium text-white/90 tracking-wide uppercase">Employer GLP-1 Benefits Plan</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-[#ffdb4d] mb-6 leading-[1.1] tracking-tight">
            <span className="relative inline-block overflow-hidden">
              <span className="relative z-10">The Wellness Benefit That</span>
              <span 
                className="absolute inset-0 bg-[#1B3A4B] z-0 animate-[slideHighlight_3s_ease-in-out_infinite]"
                style={{ transform: 'translateX(-100%)' }}
              />
            </span>
            <span className="block mt-2 relative inline-block overflow-hidden">
              <span className="relative z-10">Actually Drives Results</span>
              <span 
                className="absolute inset-0 bg-[#1B3A4B] z-0 animate-[slideHighlight_3s_ease-in-out_infinite_0.5s]"
                style={{ transform: 'translateX(-100%)' }}
              />
            </span>
          </h1>
          
          <p className="text-lg text-[#ffdb4d] font-medium mb-4 tracking-wide">
            Enterprise solutions for employers, health plans, and partners.
          </p>
          <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed">
            GLP-1s and custom peptides starting at $74/month. Transform employee health while maximizing your benefits ROI.
          </p>
          
          {/* ROI highlight */}
          <div className="flex flex-wrap items-center justify-center gap-6 mb-10">
            <div className="flex items-center gap-2 bg-green-500/20 backdrop-blur-sm border border-green-400/30 px-4 py-2 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-green-300 font-semibold">$3-$6 ROI per $1 invested</span>
            </div>
            <div className="text-white/60 text-sm hidden md:block">•</div>
            <span className="text-white/70 text-sm">Implementation in &lt;2 weeks</span>
            <div className="text-white/60 text-sm hidden md:block">•</div>
            <span className="text-white/70 text-sm">No insurance hassles</span>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={onRequestDemo}
              size="lg" 
              className="bg-[#ffdb4d] text-[#0D1B2A] hover:bg-white font-bold px-10 py-7 text-lg shadow-2xl shadow-[#ffdb4d]/20 group transition-all duration-300"
            >
              Save up to 80% vs Branded GLP-1 Plans
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              onClick={onRequestDemo}
              variant="outline"
              size="lg" 
              className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 font-semibold px-8 py-7 text-lg backdrop-blur-sm"
            >
              Schedule Executive Briefing
            </Button>
          </div>
        </div>
      </div>
      
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 to-transparent" />
    </section>
  );
};

export default EmployerHero;
